import { useEffect, useRef, useState } from 'react'
import _ from 'lodash'
import {
  Spinner,
  Button,
  Separator,
  Input,
  Grid,
} from '@design-systems/canvas-ui'
import {
  addCertificationComponentRow,
  deleteSpecificComponentRow,
} from '../../services/lcmService'
import { SectionWrapper } from '../Builders'
import CertificateDetails from './CertificateDetails'
import {
  FACTORY_REGISTRATION_LICENSE_UID,
  MANUFACTURING_TYPE_ID_PACKAGING,
  RFI_TYPE_LOCATION,
  RFI_TYPE_SUPPLIER,
} from '../../common/commonConstants'

const CERT_PATH =
  'dynamicSections.businessCapability.certifications.certificationList'
const FACTORY_REG_CERT_PATH =
  'dynamicSections.businessCapability.factory_registration_license.certificationList'

const Certifications = (props) => {
  const [sectionData, setSectionData] = useState(null)
  const [certFilePath, setCertFilePath] = useState('')
  const certificateRowKeys = useRef(new WeakMap())
  const nextCertificateRowKey = useRef(0)

  //Row addition
  const [isCertAddInProgress, setIsCertAddInProgress] = useState(false)

  //Row deletion
  const [isCertDeletionInProgress, setIsCertDeletionInProgress] =
    useState(false)
  const [deletingCertIndex, setDeletingCertIndex] = useState(-1)

  //Trigger save
  const [isTriggerSave, setIsTriggerSave] = useState(false)

  //#region UseEffects

  useEffect(() => {
    setSectionData(props.sectionData)
  }, [props.sectionData])

  useEffect(() => {
    if (isTriggerSave && !_.isEqual(props.sectionData, sectionData)) {
      props.saveRfiPayload(sectionData, props.sectionTemplate.uid)
    }
  }, [isTriggerSave]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    props.sectionTemplate.uid === FACTORY_REGISTRATION_LICENSE_UID
      ? setCertFilePath(FACTORY_REG_CERT_PATH)
      : setCertFilePath(CERT_PATH)
  }, [props.rfiType])

  //#endregion

  const getCertificateRowKey = (certificate) => {
    if (!certificate || typeof certificate !== 'object') return certificate

    let rowKey = certificateRowKeys.current.get(certificate)
    if (!rowKey) {
      rowKey = `certificate-${nextCertificateRowKey.current++}`
      certificateRowKeys.current.set(certificate, rowKey)
    }
    return rowKey
  }

  /**
   * Render certification body
   * @returns
   */
  const renderCertificationsBody = () => {
    let certReferenceKey = props.sectionTemplate.data.uid
    let certRowHeader = props.sectionTemplate.data.row_header
      ? props.sectionTemplate.data.row_header
      : 'Certifications'
    let allCerts = []
    if (sectionData) {
      let certList = sectionData[certReferenceKey]

      allCerts = certList?.map((eachCert, certIndex) => {
        return (
          <div key={getCertificateRowKey(eachCert)}>
            {/* 1. Header - Certification # */}
            <Grid.Container className="row-centre">
              <Grid.Item size={{ xs: 10 }}>
                <Input.Label className="hc8-fs-150 font-weight-500">
                  {certRowHeader + ` ${certIndex + 1}`}
                </Input.Label>
              </Grid.Item>
              {(props.rfiType.toLowerCase() === RFI_TYPE_SUPPLIER ||
                (props.rfiType.toLowerCase() === RFI_TYPE_LOCATION &&
                  certIndex > 0)) && (
                <Grid.Item size={{ xs: 2 }} className="align-self-right">
                  <Button
                    key={`btn-delete-cert-${certIndex}`}
                    data-testid={`btn-delete-cert-${certIndex}`}
                    className="section-button"
                    onClick={() => deleteCertificationUnit(certIndex)}
                    variant="secondary"
                    isDisabled={isCertAddInProgress | isCertDeletionInProgress}
                  >
                    {isCertDeletionInProgress &&
                    certIndex === deletingCertIndex ? (
                      <Spinner
                        data-testid={`spinner-delete-cert-${certIndex}`}
                        size="sm"
                      />
                    ) : (
                      `Remove`
                    )}
                  </Button>
                </Grid.Item>
              )}
            </Grid.Container>

            <CertificateDetails
              sectionIndexPath={`${props.sectionPath}.${certReferenceKey}[${certIndex}]`}
              errorCache={props.errorCache}
              rfiType={props.rfiType}
              supplierId={props.supplierId}
              locationId={props.locationId}
              certIndex={certIndex}
              certInfo={eachCert}
              rfiMasterData={props.rfiMasterData}
              updateCertDetails={updateCertDetails}
              manufacturingTypeId={props.manufacturingTypeId}
              componentPath={certFilePath}
            />
            {certIndex !== certList.length - 1 && (
              <Separator className="hc8-mb-2x" />
            )}
          </div>
        )
      })
    }
    return allCerts
  }

  /**
   * Update certification details
   * @param {*} updatedCertRow
   * @param {*} certIndex
   */
  const updateCertDetails = (updatedCertRow, certIndex) => {
    if (updatedCertRow) {
      let certReferenceKey = props.sectionTemplate.data.uid
      let certRows = sectionData[certReferenceKey].map((eachCertRow, index) => {
        return index !== certIndex ? eachCertRow : updatedCertRow
      })

      setSectionData({
        ...sectionData,
        [certReferenceKey]: certRows,
      })
    }
  }

  //#region Add/Delete row

  /**
   * Add certification row
   */
  const addCertificationsUnit = async () => {
    let certReferenceKey = props.sectionTemplate.data.uid
    setIsCertAddInProgress(true)
    let newCertItem = {
      certificate_name: null,
      other_certificate_name: null,
      issuing_organisation: null,
      issue_date: null,
      expiry_date: null,
      additional_remarks: null,
      file_upload: {
        uploaded_files: {},
      },
    }

    //Adding materials certified for packaging
    if (props.manufacturingTypeId === MANUFACTURING_TYPE_ID_PACKAGING) {
      newCertItem = { ...newCertItem, materials_certified: null }
    }

    try {
      await addCertificationComponentRow(
        props.rfiType, //rfi type
        props.supplierId, //supplier ID
        props.locationId, //location ID
        certFilePath, //cert file path
        newCertItem, //payload
        props.manufacturingTypeId, //manufacturing type id
      )
      setSectionData({
        ...sectionData,
        [certReferenceKey]: [...sectionData[certReferenceKey], newCertItem],
      })
      setIsCertAddInProgress(false)
    } catch (error) {
      setIsCertAddInProgress(false)
    }
  }

  /**
   * Delete certification row
   * @param {*} certIndex
   */
  const deleteCertificationUnit = async (certIndex) => {
    let certReferenceKey = props.sectionTemplate.data.uid
    setIsCertDeletionInProgress(true)
    setDeletingCertIndex(certIndex)
    try {
      await deleteSpecificComponentRow(
        props.rfiType, //rfi type
        props.supplierId, //supplier ID
        props.locationId, //location ID
        certFilePath, //cert file path
        certIndex, //payload,
        props.manufacturingTypeId, //manufacturing type id
      )
      let allNewCerts = [...sectionData[certReferenceKey]]
      allNewCerts.splice(certIndex, 1)
      setSectionData({
        ...sectionData,
        [certReferenceKey]: allNewCerts,
      })
      setIsCertDeletionInProgress(false)
      setDeletingCertIndex(-1)
    } catch (error) {
      setIsCertDeletionInProgress(false)
      setDeletingCertIndex(-1)
    }
  }

  //#endregion

  return (
    <SectionWrapper
      sectionTemplate={props.sectionTemplate}
      sectionBody={renderCertificationsBody()}
      setIsTriggerSave={setIsTriggerSave}
      sectionPath={props.sectionPath}
      errorCache={props.errorCache}
      addMoreMetadata={{
        isAddMore: props.sectionTemplate.add_more,
        addMoreButtonText: props.sectionTemplate.add_more?.label,
        isDisabled: isCertAddInProgress | isCertDeletionInProgress,
        isAddMoreInProgress: isCertAddInProgress,
        addMoreSubsectionReferenceKey: props.sectionTemplate.data.uid,
        addMore: (key) => addCertificationsUnit(key),
      }}
    />
  )
}

export default Certifications
